import React from 'react';
import { CalendarOff } from 'lucide-react';
import api from '../../utils/axios';
import useCardData from './useCardData';
import {
  DashboardCard, CardLoading, CardError, CardEmpty,
} from './DashboardCard';
import { formatDayRange, fullName, initials } from './format';

const WINDOW_DAYS = 14;

const TEAM_LABEL = {
  all: 'Everyone',
  department: 'Your department',
  self: 'You',
};

const PersonRow = ({ leave }) => (
  <li data-testid="dashboard-who-is-off-row" className="flex items-center gap-3 py-2.5">
    <span
      aria-hidden="true"
      className="flex-shrink-0 h-9 w-9 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold flex items-center justify-center"
    >
      {initials(leave.first_name, leave.last_name)}
    </span>
    <span className="flex-1 min-w-0">
      <span className="block text-sm font-medium text-gray-900 truncate">{fullName(leave)}</span>
      <span className="block text-xs text-gray-500 truncate">
        {formatDayRange(leave.start_date, leave.end_date)}
        {leave.position ? ` · ${leave.position}` : ''}
      </span>
    </span>
    {/* A leave over a weekend or holiday only can count no working days. */}
    {leave.business_days > 0 && (
      <span className="flex-shrink-0 text-xs font-medium text-gray-600 tabular-nums">
        {`${leave.business_days} ${leave.business_days === 1 ? 'day' : 'days'}`}
      </span>
    )}
  </li>
);

const Group = ({ label, leaves, testId }) => (
  <div data-testid={testId}>
    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</h3>
    <ul className="divide-y divide-gray-100">
      {leaves.map((leave) => <PersonRow key={leave.id} leave={leave} />)}
    </ul>
  </div>
);

/**
 * Teammates on approved leave today and over the next two weeks
 * (GET /api/dashboard/who-is-off). The server decides who the team is and sends names
 * and dates only.
 */
const WhoIsOffCard = ({ className = '' }) => {
  const result = useCardData(
    async () => (await api.get('/api/dashboard/who-is-off', { params: { days: WINDOW_DAYS } })).data.data,
    []
  );
  const data = result.data;
  const offToday = data ? data.leaves.filter((leave) => leave.start_date <= data.from) : [];
  const upcoming = data ? data.leaves.filter((leave) => leave.start_date > data.from) : [];
  const teamLabel = data && TEAM_LABEL[data.team];

  return (
    <DashboardCard
      data-testid="dashboard-who-is-off"
      title="Who is off"
      icon={CalendarOff}
      subtitle={teamLabel ? `${teamLabel} · next ${WINDOW_DAYS} days` : undefined}
      className={className}
    >
      {result.loading && <CardLoading label="Loading who is off" />}
      {result.error && (
        <CardError
          data-testid="dashboard-who-is-off-error"
          message="Could not load who is off."
          onRetry={result.reload}
        />
      )}
      {data && data.leaves.length === 0 && (
        <CardEmpty icon={CalendarOff} data-testid="dashboard-who-is-off-empty">
          {`Nobody is off in the next ${WINDOW_DAYS} days.`}
        </CardEmpty>
      )}
      {data && data.leaves.length > 0 && (
        <div className="space-y-4 max-h-80 overflow-y-auto pr-1">
          {offToday.length > 0 && <Group label="Today" leaves={offToday} testId="dashboard-who-is-off-today" />}
          {upcoming.length > 0 && <Group label="Coming up" leaves={upcoming} testId="dashboard-who-is-off-upcoming" />}
        </div>
      )}
      {data && data.truncated && (
        <p data-testid="dashboard-who-is-off-truncated" className="mt-3 text-xs text-gray-500">
          {`Showing the first ${data.leaves.length} of ${data.total}.`}
        </p>
      )}
    </DashboardCard>
  );
};

export default WhoIsOffCard;
